import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CategoryBreadcrumb from '@/components/feature/CategoryBreadcrumb';
import { getArticleById, getHotArticles } from '@/mocks/articles';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function ArticlePage() {
  const { id } = useParams();
  const article = getArticleById(id || '');
  const [showTip, setShowTip] = useState(false);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-file-damage-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-2xl font-bold font-heading text-foreground-900 mb-2">文章不存在</h1>
            <p className="text-foreground-500 mb-6">该爆料内容暂时无法查看</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              返回首页 <i className="ri-home-4-line"></i>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const related = getHotArticles(3).filter(a => a.id !== article.id);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const viewsText = article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : String(article.views);

  return (
    <>
      <Navbar />
      <main className="bg-background-50">
        {/* Article Header */}
        <section className="pt-16 pb-8 md:pb-12 px-4 md:px-6 border-b border-background-200">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6 pt-6">
              <CategoryBreadcrumb categorySlug={article.categorySlug} articleTitle={article.title} />
            </div>
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 leading-snug mb-5">
              {article.title}
            </h1>
            <div className="flex items-center flex-wrap gap-x-5 gap-y-2 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {formatDate(article.publishedAt)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {viewsText} 次阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-chat-3-line"></i>
                {article.comments} 条评论
              </span>
            </div>
          </div>
        </section>

        {/* Article Cover Image */}
        <section className="px-4 md:px-6 mb-8 md:mb-12">
          <div className="max-w-3xl mx-auto">
            <div className="rounded-xl overflow-hidden aspect-[16/9]">
              <img
                src={article.coverImage}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover object-top"
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="px-4 md:px-6 pb-12 md:pb-16">
          <div className="max-w-3xl mx-auto">
            <div className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content || '<p>暂无正文内容</p>' }}
            />

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="flex items-center flex-wrap gap-2 mt-8">
                <span className="text-sm text-foreground-400 mr-1">标签：</span>
                {article.tags.map(tag => (
                  <Link
                    key={tag}
                    to={`/category/${article.categorySlug}`}
                    className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-600 border border-background-200 hover:bg-secondary-100 hover:text-secondary-700 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Related Articles */}
        {related.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100 border-t border-background-200">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="space-y-4">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 transition-all duration-300 hover:scale-[1.01]"
                  >
                    <div className="w-20 h-14 rounded-md overflow-hidden flex-shrink-0">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-accent-600 font-medium mb-1">{rel.category}</p>
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
                        {rel.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-fire-line"></i>
                          {rel.hotIndex}°
                        </span>
                        <span>{rel.publishedAt.slice(5)}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Tip Button */}
        <section className="py-8 md:py-10 px-4 md:px-6 text-center bg-background-50">
          <div className="max-w-3xl mx-auto">
            <button
              onClick={() => setShowTip(!showTip)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary-300 text-primary-700 text-sm font-medium hover:bg-primary-50 transition-colors"
            >
              <i className="ri-lightbulb-line"></i>
              我有爆料线索
            </button>
            {showTip && (
              <div className="mt-4 p-4 rounded-lg bg-secondary-100 text-sm text-secondary-800 text-left">
                <p className="font-medium mb-2">投稿方式：</p>
                <ul className="space-y-1 text-secondary-700">
                  <li>· 邮箱：tips@4xhyp.com</li>
                  <li>· 微信：911baoliao</li>
                  <li>· 请务必附上相关截图或视频</li>
                  <li>· 我们承诺保护爆料人隐私</li>
                </ul>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishedAt,
            dateModified: article.publishedAt,
            image: article.coverImage,
            publisher: {
              '@type': 'Organization',
              name: '911爆料网',
              logo: { '@type': 'ImageObject', url: 'https://4xhyp.com/logo.png' },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://4xhyp.com/article/${article.id}`,
            },
            articleSection: article.category,
            keywords: article.tags?.join(', '),
          }),
        }}
      />
    </>
  );
}
